<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { SELF_LANGUAGE, isSameLangMarker } from '../preferences';

const props = defineProps({
  messageId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
  content: { type: String, default: '' },
  contentAttributes: { type: Object, default: () => ({}) },
  // 'inbound' = customer messages; 'outbound' = public agent/AI bubbles
  direction: { type: String, default: 'inbound' },
});

const { t } = useI18n();
const store = useStore();

const prefs = computed(() => store.getters.prefsFor(props.conversationId));

const featureOn = computed(() =>
  props.direction === 'outbound'
    ? !!prefs.value.outboundAutoTranslate
    : !!prefs.value.inboundOn
);

const translatedText = computed(() => {
  const cached = props.contentAttributes?.ai_translations || {};
  return (
    cached[SELF_LANGUAGE] ||
    cached['zh-CN'] ||
    cached.zh_CN ||
    cached.zh ||
    store.getters.translationFor(props.messageId) ||
    ''
  );
});

const isSameLang = computed(() => isSameLangMarker(translatedText.value));

const isUntranslated = computed(
  () => featureOn.value && !translatedText.value && !!props.content
);

const requestTranslation = () => {
  if (!featureOn.value) return;
  if (translatedText.value) return;
  if (!props.content) return;
  const action =
    props.direction === 'outbound' ? 'translateOutbound' : 'translateInbound';
  store.dispatch(action, {
    conversationId: props.conversationId,
    messageIds: [props.messageId],
  });
};

onMounted(requestTranslation);
watch(featureOn, requestTranslation);
</script>

<template>
  <div
    v-if="featureOn && content"
    class="translation-line mt-1.5 pt-1.5 border-t border-n-alpha-2"
  >
    <div class="text-[10px] uppercase tracking-wide text-n-slate-10 mb-0.5">
      {{ t('CUSTOM.TRANSLATION.TRANSLATION_LABEL') }}
    </div>
    <div
      v-if="isSameLang"
      class="text-xs italic text-n-slate-10"
    >
      {{
        t('CUSTOM.TRANSLATION.ALREADY_SELF_LANG', {
          lang: t('CUSTOM.TRANSLATION.SELF_LANG_SHORT'),
        })
      }}
    </div>
    <div v-else-if="translatedText" class="text-sm whitespace-pre-wrap">
      {{ translatedText }}
    </div>
    <div v-else-if="isUntranslated" class="text-xs italic text-n-slate-10">
      {{ t('CUSTOM.TRANSLATION.TRANSLATING') }}
    </div>
  </div>
</template>
